/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const AddEcuTypeModal = ({ open, onClose, onSave, existingMakes = [] }) => {
  const [ecuTypeForm, setEcuTypeForm] = useState({
    make: "",
    ecuType: "",
  });
  const [makeError, setMakeError] = useState(false);
  const [ecuTypeError, setEcuTypeError] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setEcuTypeForm({
        make: "",
        ecuType: "",
      });
      setMakeError(false);
      setEcuTypeError(false);
    }
  }, [open]);

  const handleClose = () => {
    setEcuTypeForm({
      make: "",
      ecuType: "",
    });
    setMakeError(false);
    setEcuTypeError(false);
    onClose();
  };

  const handleSave = () => {
    const isMakeValid = ecuTypeForm.make.trim() !== "";
    const isEcuTypeValid = ecuTypeForm.ecuType.trim() !== "";

    setMakeError(!isMakeValid);
    setEcuTypeError(!isEcuTypeValid);

    if (isMakeValid && isEcuTypeValid) {
      onSave(ecuTypeForm);
      handleClose();
    }
  };
console.log("ecu type form data in modal:", ecuTypeForm)
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      slotProps={{
        paper: {
          sx: {
            borderRadius: "8px",
            padding: "8px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          fontSize: "20px",
          fontWeight: 500,
        }}
      >
        Add ECU Type
        <IconButton onClick={handleClose} sx={{ padding: "4px" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "0 24px 24px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Make and ECU Type Fields in a Row */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Make Field */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  marginBottom: "8px",
                  color: "#121415",
                }}
              >
                Make <span style={{ color: "#D32F2F" }}>*</span>
              </Typography>
              <FormControl fullWidth error={makeError}>
                <Select
                  displayEmpty
                  value={ecuTypeForm.make}
                  onChange={(e) => {
                    const selectedMake = e.target.value;
                    setEcuTypeForm({
                      ...ecuTypeForm,
                      make: selectedMake,
                    });
                    if (makeError && selectedMake) {
                      setMakeError(false);
                    }
                  }}
                  sx={{
                    height: "40px",
                    fontSize: "14px",
                    "& .MuiSelect-select": {
                      padding: "8px 14px",
                    },
                    "& .MuiSelect-select.MuiSelect-placeholder": {
                      color: "#717273",
                    },
                  }}
                  renderValue={(selected) =>
                    selected || <span style={{ color: "#717273" }}>Eg: Ford</span>
                  }
                >
                  {existingMakes.map((make) => (
                    <MenuItem
                      key={make}
                      value={make}
                      sx={{
                        minHeight: "32px",
                        fontSize: "14px",
                        padding: "4px 14px",
                      }}
                    >
                      {make}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{makeError ? "Make is required" : ""}</FormHelperText>
              </FormControl>
            </Box>

            {/* ECU Type Field */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  marginBottom: "8px",
                  color: "#121415",
                }}
              >
                ECU Type <span style={{ color: "#D32F2F" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Eg: ABS"
                value={ecuTypeForm.ecuType}
                onChange={(e) => {
                  setEcuTypeForm({
                    ...ecuTypeForm,
                    ecuType: e.target.value,
                  });
                  if (ecuTypeError && e.target.value) {
                    setEcuTypeError(false);
                  }
                }}
                error={ecuTypeError}
                helperText={ecuTypeError ? "ECU Type is required" : ""}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "40px",
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 14px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#717273",
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: 0,
          paddingTop: "24px",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            padding: "8px 24px",
            fontSize: "14px",
            fontWeight: 500,
            height: "36px",
            color: "#666666",
            borderColor: "#E0E0E0",
            backgroundColor: "#F5F5F5",
            "&:hover": {
              backgroundColor: "#EEEEEE",
              borderColor: "#E0E0E0",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            textTransform: "none",
            padding: "8px 24px",
            fontSize: "14px",
            fontWeight: 500,
            height: "36px",
            backgroundColor: "#0476E2",
            "&:hover": {
              backgroundColor: "#0356a6",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddEcuTypeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  existingMakes: PropTypes.arrayOf(PropTypes.string),
};

AddEcuTypeModal.defaultProps = {
  existingMakes: [],
};

export default AddEcuTypeModal;
